import React from "react"
import tw, { css } from "twin.macro"
import { StaticImage } from "gatsby-plugin-image"
import Layout from "../components/layout"
import { GatsbySeo, BreadcrumbJsonLd } from "gatsby-plugin-next-seo"

// import icon-svg
import { ReactComponent as Right } from "../images/e-fax/right.svg"
import { ReactComponent as Fault } from "../images/e-fax/fault.svg"
import { ReactComponent as Faxing } from "../images/ring-central/free-faxing.svg"
import { ReactComponent as Time } from "../images/ring-central/time-saving.svg"
import { ReactComponent as Security } from "../images/ring-central/security.svg"
import { ReactComponent as Number } from "../images/ring-central/number-porting.svg"
import { ReactComponent as Haopin } from "../images/e-fax/haopin.svg"
import { ReactComponent as Previous } from "../images/e-fax/previous.svg"
import { ReactComponent as Star0 } from "../images/e-fax/star-0.svg"
import { ReactComponent as Star1 } from "../images/e-fax/star-1.svg"
import headBg from '../images/e-fax/head-bg.png'

// import Swiper JS
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Pagination, Autoplay } from "swiper/core"
// import Swiper styles
import "swiper/swiper-bundle.css"
import "swiper/components/navigation/navigation.min.css"
// install Swiper's Pagination  component
SwiperCore.use([Pagination, Autoplay])

// use twin.macro to style
// use twin.macro to style
const styles = {
  headBg: [
    tw`bg-cover bg-no-repeat text-white w-full`, css` background: url(${headBg});background-size:100% 100%`
  ],
  // foot-bg gradient
  footBg: [
    css`&:after {content: "";background: linear-gradient(-188deg,#fff8f8 21.6%,rgba(227, 243, 255, 0.777331) 39.8%,rgba(255, 255, 255, 0) 65.59%);width: 100%;height: 100%;position: absolute;left: 0;top: 0;transform: rotate(180deg);}`
  ],
}

export default function EFax({ location }) {
  return (
    <Layout>
      <GatsbySeo
        canonical={location.href}
        title="An Easy, Affordable Alternative to RingCentral Fax | CocoFax"
        description="As one of the earliest and top performers in the online fax industry, RingCentral has attracted mixed reviews irrespective of its renown. CocoFax is now providing an easier-to-use alternative with more flexible plans available. Read this page to find out more."
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Home',
            item: process.env.REACT_APP_CONFIG_MAINURL,
          },
          {
            position: 2,
            name: 'Alternatives',
            item: `${process.env.REACT_APP_CONFIG_MAINURL}/alternatives.html`,
          },
          {
            position: 3,
            name: 'RingCentral Alternative',
            item: location.href,
          },
        ]}
      />
      {/* head */}
      <section css={[styles.headBg]}>
        <div className='mx-auto max-w-[1116px] px-4'>
          <ul className="text-sm sm:text-xs pt-[10px] flex flex-wrap items-center leading-6">
            <li><a href="/" target="_blank">Home</a> &nbsp;&gt;&nbsp;</li>
            <li><a href="/alternatives.html" target="_blank">Alternatives</a> &nbsp;&gt;&nbsp;</li>
            <li css={[css`color:rgba(255,255,255,0.6)`]}>RingCentral Alternative</li>
          </ul>
          <div className=" relative pt-[88px] pb-[96px] lg:pt-10 lg:pb-[45px] lg:text-center">
            <h1 className="font-extrabold text-[40px] md:text-[calc(24px+2vw)] leading-snug max-w-[640px] lg:mx-auto">
              CocoFax: A More Cost-effective Alternative to RingCentral Fax
            </h1>
            <p className="pt-4 max-w-[640px] lg:mx-auto">
              CocoFax is a holistic approach to <a href="/" className="text-green hover:text-green hover:underline" target="_blank">online faxing</a>, a great alternative
              to RingCentral Fax. RingCentral’s fax component is just a part of
              its VoIP empire, whose strength lies primarily in its VoIP, non-fax
              features. While CocoFax is more fax-oriented and always trumps
              RingCentral when it comes to fax capability. If you also care about
              cost-effectiveness, simplicity, and customer support, then CocoFax
              is certainly your best bet.
            </p>
            <div className="relative max-w-[200px] lg:mx-auto mt-10 lg:mt-5 cursor-pointer">
              <div className="text-center bg-[#F77062] hover:bg-[#ff5656] font-bold md:font-semibold shadow-redButton rounded-[3px] leading-[52px] px-5 md:px-2 block">
                <a
                  href="/choose-fax-number.html#/"
                  className="hover:text-white"
                >
                  Start Faxing
                </a>
              </div>
              <p className="leading-8 text-xs text-center text-white">
                30-Day Money-Back Guarantee
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* /head */}

      {/* content01 - CocoFax is More Versatile Than eFax */}
      <section
        css={[
          tw`mx-auto relative pb-[62px] lg:pb-10`,
          css`
            background: linear-gradient(
              158.91deg,
              #fff8f8 14.74%,
              rgba(227, 243, 255, 0.777331) 36.6%,
              rgba(255, 255, 255, 0) 84.39%
            );
          `,
        ]}
      >
        <div className="max-w-[1116px] w-full px-4 mx-auto pt-[96px] lg:pt-[45px] relative z-[1] text-center">
          <div className="rounded-full mx-auto text-center bg-green bg-opacity-[0.06] text-sm font-bold md:text-base text-green py-[6px] px-5 md:py-1 md:px-2 inline-block">
            Features & Pricing
          </div>
          <h2 className="text-black text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center mt-5 leading-tight">
            CocoFax Charges Less but Offers More
          </h2>
          <p className="text-gray-dark text-center mt-6 lg:mt-4 leading-7">
            Compared to RingCentral, CocoFax is a more reasonable
            budget-friendly alternative that doesn’t skimp out on quality. In
            general, CocoFax offers virtually all RingCentral's fax functions,
            whereas the latter lacks many of our killer features, such as the
            option to archive all your faxes to Google Drive, enable 2-factor
            verification for optimal security, or to create contacts groups for
            faster faxing.
          </p>
        </div>
        <div class="max-w-[852px] relative mt-[60px] lg:mt-[30px] mx-auto px-4">
          <h3 class="mx-auto text-center font-bold text-lg">Side by Side Comparison</h3>
          <table class="relative z-[1] mt-10 w-full border-collapse border border-[#EFF3F9] mx-auto shadow-tableItem rounded bg-white">
            <thead>
              <tr>
                <th class="w-1/2 md:w-1/2 border border-[#EFF3F9] text-left pl-[13px] h-12 leading-5 text-[14px] font-extrabold">
                  Features
                </th>
                <th class="w-1/4 md:w-1/4 border border-[#EFF3F9] leading-5 text-[14px] font-extrabold">
                  CocoFax
                </th>
                <th class="w-1/4 md:w-1/4 border border-[#EFF3F9] leading-5 text-[14px] font-extrabold">
                  RingCentral Fax
                </th>
              </tr>
            </thead>
            <tbody className="text-center pl-10">
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/free-fax.html"
                    className="hover:underline text-blue"
                    target="_blank"
                  >
                    Free Fax </a>
                  (No Credit Card Required)
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Fault width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Entry-Level Plan
                </td>
                <td class="border border-[#EFF3F9] text-sm">$7.99/user per month</td>
                <td class="border border-[#EFF3F9] text-sm">$17.99/user per month</td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Advanced Plan
                </td>
                <td class="border border-[#EFF3F9] text-sm">$29.99/user per month</td>
                <td class="border border-[#EFF3F9] text-sm">$37.99/user per month</td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/fax-number-porting.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Fax Number Porting
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/fax-cover-sheet.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Customizable cover pages
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Fault width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/email-to-fax.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Email to Fax
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  2-Factor Authentication
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Fault width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Multiple Fax Recipients
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  International Coverage
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9] text-red text-sm">Limited</td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  MultipleTeam Members
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/fax-software/android-fax.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Android Mobile Apps
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/slack-integration.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Slack Integration
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/fax-software/microsoft-intergration-fax.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Microsoft Add-on
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="https://chrome.google.com/webstore/detail/cocofax-send-receive-fax/aamhfladfpacdednbomhcfgapoljdjgo"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    Chrome Extension
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/google-integration.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    G Suite Add-on
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Automatically Archive to Google Drive
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Fault width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Blacklist
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Configure Retry Fax Settings
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Fault width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Scheduled Faxing
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  <a
                    href="/developers/api.html"
                    target="_blank"
                    className="hover:underline text-blue"
                  >
                    API
                  </a>
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  SMS Notifications
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
              </tr>
              <tr>
                <td class="border border-[#EFF3F9] h-12 text-left pl-[13px] font-semibold text-[14px]">
                  Add Contacts to Group Labels
                </td>
                <td class="border border-[#EFF3F9]">
                  <Right width={18} className="mx-auto" />
                </td>
                <td class="border border-[#EFF3F9]">
                  <Fault width={18} className="mx-auto" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      {/* content01 - CocoFax is More Versatile Than eFax */}

      {/* content02 - How Do Customers Like eFax and CocoFax */}
      <section className="pt-[59px] lg:pt-10 pb-[96px] lg:pb-10 max-w-[1116px] w-full mx-auto px-4">
        <h2 className="text-black text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center leading-tight">
          What do customers say?
        </h2>
        <div className="mt-10 lg:mt-5 max-w-[992px] px-4 flex items-center justify-between lg:flex-col">
          <div className="max-w-[534px] switch lg:w-full">
            <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
              Expensive and buggy
            </h3>
            <Swiper
              // rewrite swiper pagination style
              pagination={{
                clickable: true,
                bulletClass:
                  "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
              }}
              bulletActiveClass={"text-red"}
              loop={true}
              spaceBetween={40}
              allowTouchMove={true}
              autoHeight={true}
              className="!pb-10 relative mt-10 lg:mt-3 !px-5"
            >
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center ">
                      "The application will often have failed sent messages more often then the online program. The faxes will sometimes go through slower as well.”
                    </p>
                    <a
                      href="https://www.capterra.com/p/132178/RingCentral-Office/reviews/1196930/"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “I disliked the price. I felt as if it could have been lower then what it currently is now.”
                    </p>
                    <a
                      href="https://www.g2.com/products/ringcentral-mvp/reviews/ringcentral-mvp-review-1359822"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “a bit pricey, this has an added cost of busimess.”
                    </p>
                    <a
                      href="https://www.g2.com/products/ringcentral-mvp/reviews/ringcentral-mvp-review-606644"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
          <div className="max-w-[534px] switch lg:w-full -ml-2 lg:ml-0">
            <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
              Versatile and convenient
            </h3>
            <Swiper
              pagination={{
                clickable: true,
                bulletClass:
                  "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
              }}
              loop={true}
              spaceBetween={40}
              allowTouchMove={true}
              autoHeight={true}
              className="!pb-10 relative mt-10 lg:mt-3 !px-5"
            >
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center ">
                      “CocoFax is an excellent tool for teamwork and for doing business with your partners. CocoFax allows multiple members to share a single account and send multiple faxes simultaneously. It's really convenient.”
                    </p>
                    <a
                      href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4857176"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      "Cocofax is easy to use. Excellent for extensive or limited use."
                    </p>
                    <a
                      href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4857143"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “This has been valuable for my business to get information to those that only accept faxed request. It worked when I needed it to and the fact the fax is through the computer allows flexibility on ways to save and move documents.”
                    </p>
                    <a
                      href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4817003"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </section>

      <section className="bg-blue-light">
        <div className="max-w-[1116px] px-4 w-full mx-auto pt-[96px] lg:pt-[44px] relative z-[1]">
          {/*          <div className="text-center">
            <div className="rounded-full mx-auto text-center bg-green bg-opacity-[0.06] text-green text-sm font-bold py-[6px] px-5 md:py-1 md:px-2 md:text-base inline-block">
              User Interface
            </div>
          </div>
          <h2 className="text-black text-[36px] md:text-[calc(24px+1.5vw)] font-extrabold text-center mt-4 leading-snug">
            CocoFax Offers More Pricing Plans That Suit All Pockets
          </h2>
          <p className="text-gray-dark text-center mt-6 lg:mt-3 leading-7">
            RingCentral offers only 2 pricy plans mainly for mid-volume faxers, with literally no consideration for occasional, light, and heavy faxers. By comparison, CocoFax’s wide range of plans and packages factors, and more. An in various demands of different fax users, scaling up from 10 pages, 60 pages, to 200 pages, 400 pages, to 1200 pagesyone with faxing needs can find a suitable and affordable plan with CocoFax. 
          </p>*/}
        </div>
        <div className="max-w-[1080px] w-full px-4 flex justify-between items-center lg:flex-col-reverse mx-auto">
          <div className="w-[610px] lg:w-full switch lg:mt-3">
            <Swiper
              // rewrite swiper pagination style
              pagination={{
                clickable: true,
                bulletClass:
                  "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
              }}
              bulletActiveClass={"text-red"}
              loop={true}
              spaceBetween={40}
              allowTouchMove={true}
              autoHeight={true}
              className="!pb-10 relative !px-5"
            >
              <SwiperSlide>
                <figure className="shadow-block">
                  <StaticImage
                    src="../images/ring-central/cocofax-pricing.png"
                    width={1136}
                    alt="CocoFax’s pricing"
                  />
                </figure>
              </SwiperSlide>
              <SwiperSlide>
                <figure className="shadow-block">
                  <StaticImage
                    src="../images/ring-central/ringcentral-pricing.png"
                    width={1136}
                    alt="RingCentral’s pricing"
                  />
                </figure>
              </SwiperSlide>
            </Swiper>
          </div>
          <div className="max-w-[440px] relative z-[1] ml-5 lg:ml-0 lg:w-full">
            <h3 className="font-extrabold text-[28px] md:text-[calc(18px+1.8vw)] leading-10 lg:text-center">
              CocoFax Offers More Pricing Plans That Suit All Pockets
            </h3>
            <p className="mt-[18px] lg:mt-0 leading-7 text-gray-dark lg:text-center text-sm">
              RingCentral offers only 2 pricy plans mainly for mid-volume faxers, with literally no consideration for occasional, light, and heavy faxers. By comparison, CocoFax’s wide range of plans and packages factors, and more. An in various demands of different fax users, scaling up from 10 pages, 60 pages, to 200 pages, 400 pages, to 1200 pagesyone with faxing needs can find a suitable and affordable plan with CocoFax.
            </p>
          </div>
        </div>
        {/* CocoFax Is Much More Focused on Fax Capability */}
        <div className="mt-[66px] lg:mt-5 pb-[88px] lg:pb-9 max-w-[1080px] w-full px-4 mx-auto text-center">
          <h2 className="text-black leading-tight text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center mt-[18px]">
            CocoFax Is Much More Focused on Fax Capability
          </h2>
          <p className="mt-[18px] lg:mt-0 leading-7 text-gray-dark lg:text-center text-sm">
            RingCentral is a family of tools for business communications. It’s distracting VoIP features are useless to most faxers and quite hamper their fax journey. As opposed to its base plan, RingCentral’s advanced suite only adds more non-fax features, so those expecting extra fax capability to come with their costlier purchase will only be disappointed.
          </p>
          <p className="mt-3 leading-7 text-gray-dark lg:text-center text-sm">
            In contrast, CocoFax is a dedicated fax platform that is designed specifically for faxing needs. You’ll be able to utilize more value-added fax features as you upgrade your plan, say two-factor authentication.
          </p>
        </div>
      </section>

      {/* content06 - A More User-friendly Design */}
      <section className="mx-auto pt-[96px] lg:pt-10 bg-gray-lightest">
        <div className="max-w-[1116px] px-4 w-full mx-auto">
          <div className="text-center">
            <div className="rounded-full mx-auto text-center bg-green bg-opacity-[0.06] text-sm font-bold text-green py-[6px] px-5 md:py-1 md:px-2 md:text-base inline-block">
              Interface
            </div>
          </div>
          <h2 className="text-black leading-tight text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center mt-[18px]">
            CocoFax Outstrips RingCentral Fax in Interface
          </h2>
          <p className="text-gray-dark text-center mt-9 lg:mt-5 leading-7">
            CocoFax’s surface always directs users straightforwardly to wherever they want, making sure there is as little interaction with the website itself as possible on the users’ side. Unlike RingCentral, CocoFax doesn’t offer VoIP features, and that does make for a simpler interface to handle your faxing tasks. While Ring Central's end-users have been complaining about its complicated, over-loaded, and perplexing web design.
          </p>
        </div>
        <div className="mt-16 lg:mt-5 flex items-center justify-center xl:flex-col">
          <div className="max-w-[1272px] px-4 switch xl:w-full">
            <div>
              <Swiper
                // rewrite swiper pagination style
                pagination={{
                  clickable: true,
                  bulletClass:
                    "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
                }}
                loop={true}
                spaceBetween={40}
                allowTouchMove={true}
                autoHeight={true}
                className="!pb-10 !px-5"
              >
                <SwiperSlide>
                  <div className="flex lg:flex-col">
                    <div>
                      <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                        RingCentral
                      </h3>
                      <figure className="rounded-lg shadow-block w-[500px] lg:w-full mt-6 lg:mt-2">
                        <StaticImage
                          src="../images/ring-central/ringcentral-01.png"
                          width={1000}
                          alt="RingCentral"
                        />
                      </figure>
                    </div>
                    <div className="ml-5 lg:ml-0 lg:pt-10">
                      <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                        CocoFax
                      </h3>
                      <figure className="rounded-lg shadow-block w-[680px] lg:w-full mt-6 lg:mt-2">
                        <StaticImage
                          src="../images/ring-central/cocofax-01.png"
                          width={1360}
                          alt="CocoFax"
                        />
                      </figure>
                    </div>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="flex lg:flex-col">
                    <div>
                      <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                        RingCentral
                      </h3>
                      <figure className="rounded-lg shadow-block w-[500px] lg:w-full mt-6 lg:mt-2">
                        <StaticImage
                          src="../images/ring-central/ringcentral-02.png"
                          width={1000}
                          alt="RingCentral"
                        />
                      </figure>
                    </div>
                    <div className="ml-5 lg:ml-0 lg:pt-10">
                      <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                        CocoFax
                      </h3>
                      <figure className="rounded-lg shadow-block w-[680px] lg:w-full mt-6 lg:mt-2">
                        <StaticImage
                          src="../images/ring-central/cocofax-02.png"
                          width={1360}
                          alt="CocoFax"
                        />
                      </figure>
                    </div>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="flex lg:flex-col">
                    <div>
                      <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                        RingCentral
                      </h3>
                      <figure className="rounded-lg shadow-block w-[500px] lg:w-full mt-6 lg:mt-2">
                        <StaticImage
                          src="../images/ring-central/ringcentral-03.png"
                          width={1000}
                          alt="RingCentral"
                        />
                      </figure>
                    </div>
                    <div className="ml-5 lg:ml-0 lg:pt-10">
                      <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                        CocoFax
                      </h3>
                      <figure className="rounded-lg shadow-block w-[680px] lg:w-full mt-6 lg:mt-2">
                        <StaticImage
                          src="../images/ring-central/cocofax-03.png"
                          width={1360}
                          alt="CocoFax"
                        />
                      </figure>
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-[120px] lg:mt-10 pb-[96px] lg:pb-10 max-w-[1116px] w-full mx-auto px-4">
        <h2 className="text-black text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center leading-tight">
          What Do Customers Say About RingCentral & CocoFax
        </h2>
        <div className="mt-10 lg:mt-5 max-w-[992px] px-4 flex items-center justify-between lg:flex-col">
          <div className="max-w-[534px] switch lg:w-full">
            <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
              Messy and confusing
            </h3>
            <Swiper
              // rewrite swiper pagination style
              pagination={{
                clickable: true,
                bulletClass:
                  "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
              }}
              bulletActiveClass={"text-red"}
              loop={true}
              spaceBetween={40}
              allowTouchMove={true}
              autoHeight={true}
              className="!pb-10 relative mt-10 lg:mt-3 !px-5"
            >
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center ">
                      “If you use the RingCentral app, you already know it's kind of a mess. A bunch of things crammed into one, vague icons, just not the easiest to maneuver around in.”
                    </p>
                    <a
                      href="https://www.g2.com/products/ringcentral-mvp/reviews/ringcentral-mvp-review-857099"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “They also have a confusing interface for adding new phones and users. It is overly complicated.”
                    </p>
                    <a
                      href="https://www.g2.com/products/ringcentral-mvp/reviews/ringcentral-mvp-review-4583122"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                    <Star0 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “The interface is a little intimidating until you have worked with it for awhile. The tutorials were outdated when I began.”
                    </p>
                    <a
                      href="https://www.capterra.com/p/132178/RingCentral-Office/reviews/425948/"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
          <div className="max-w-[534px] switch lg:w-full -ml-2 lg:ml-0">
            <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
              Easy and intuitive
            </h3>
            <Swiper
              pagination={{
                clickable: true,
                bulletClass:
                  "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
              }}
              loop={true}
              spaceBetween={40}
              allowTouchMove={true}
              autoHeight={true}
              className="!pb-10 relative mt-10 lg:mt-3 !px-5"
            >
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center ">
                      “I find CocoFax to be relatively easy to set up and use. I'm not used to using an online fax service and was worried about the amount of time and effort it would take to make it work. In actuality, it took no time at all.”
                    </p>
                    <a
                      href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4823216"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “CocoFax is easy to use, fully functional and meets our needs, and its system is very intuitive. Our users can easily browse the fax system, and most importantly, the customer support is very timely and professional.”
                    </p>
                    <a
                      href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4852152"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                  <div className="w-[240px] h-8 mx-auto flex">
                    <Star1 width={33} />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                    <Star1 width={33} className="ml-5" />
                  </div>
                  <div className="min-h-[117px] lg:min-h-0 items-center mt-8 lg:mt-5">
                    <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                      “Easy interface. Rapid addition of a range of document types.”
                    </p>
                    <a
                      href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4821146"
                      className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                    >
                      Verified G2 Review
                    </a>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </section>
      {/* /content05 - CocoFax - What Do Our Customers Say About Us */}

      <section
        css={[
          tw`mx-auto relative`,
          css`
            background: linear-gradient(
              158.91deg,
              #fff8f8 14.74%,
              rgba(227, 243, 255, 0.777331) 36.6%,
              rgba(255, 255, 255, 0) 84.39%
            );
          `,
        ]}
      >
        <div className="max-w-[1116px] w-full px-4 mx-auto pt-[96px] lg:pt-[45px] relative z-[1] text-center">
          <div className="rounded-full mx-auto text-center bg-green bg-opacity-[0.06] text-sm font-bold md:text-base text-green py-[6px] px-5 md:py-1 md:px-2 inline-block">
            Customer Support
          </div>
          <h2 className="text-black text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center mt-5 leading-tight">
            CocoFax Outdoes RingCentral Fax in Customer Support
          </h2>
          <p className="text-gray-dark text-center mt-6 lg:mt-4 leading-7">
            RingCentral’s 24/7 support is only restricted to purchasers of its advanced plan via phone and support ticket system. By comparison, CocoFax troubleshoots for all of its customers with around-the-clock support via multiple channels such as Zendesk, email, and live chat. Support agents at CocoFax will reply to our users in less than One minute.
          </p>
        </div>


        <div className="mt-[120px] lg:mt-10 pb-[96px] lg:pb-10 max-w-[1116px] w-full mx-auto px-4">
          <h2 className="text-black text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center leading-tight">
            What Do Customers Say?
          </h2>
          <div className="mt-10 lg:mt-5 max-w-[992px] px-4 flex items-center justify-between lg:flex-col">
            <div className="max-w-[534px] switch lg:w-full">
              <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                Unhelpful and terrible
              </h3>
              <Swiper
                // rewrite swiper pagination style
                pagination={{
                  clickable: true,
                  bulletClass:
                    "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
                }}
                bulletActiveClass={"text-red"}
                loop={true}
                spaceBetween={40}
                allowTouchMove={true}
                autoHeight={true}
                className="!pb-10 relative mt-10 lg:mt-3 !px-5"
              >
                <SwiperSlide>
                  <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                    <div className="w-[240px] h-8 mx-auto flex">
                      <Star1 width={33} />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                    </div>
                    <div className="min-h-[155px] lg:min-h-0 items-center mt-8 lg:mt-5">
                      <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center ">
                        “TERRIBLE customer support. Everytime I call I end up getting a call center in the Philippines. After 5 or so minutes of verifying account information and basic troubleshooting, my support tickets are never resolved. I was promised a call back multiple times, never once was called back. Please avoid if you value your time and sanity..”
                      </p>
                      <a
                        href="https://www.g2.com/products/ringcentral-mvp/reviews/ringcentral-mvp-review-470030"
                        className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                      >
                        Verified G2 Review
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[80px] lg:pb-10 pt-[34px] lg:pt-5">
                    <div className="w-[240px] h-8 mx-auto flex">
                      <Star1 width={33} />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                    </div>
                    <div className="min-h-[155px] lg:min-h-0 items-center mt-8 lg:mt-5">
                      <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                        “We experienced poor customer service from Ring Central a few separate times. The people were nice, but the time frames they expected us to wait while they attempted to track down our connection issue, was a bit over 6 hours per incident.”
                      </p>
                      <a
                        href="https://www.capterra.com/p/132178/RingCentral-Office/reviews/549006/"
                        className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                      >
                        Verified Capterra Review
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[98px] lg:pb-10 pt-[34px] lg:pt-5">
                    <div className="w-[240px] h-8 mx-auto flex">
                      <Star1 width={33} />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                      <Star0 width={33} className="ml-5" />
                    </div>
                    <div className="min-h-[155px] lg:min-h-0 items-center mt-8 lg:mt-5">
                      <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                        “The total disregard for our concerns and issues. The customer service is sorely lacking and I feel we were lied to just to get our foot in the door.”
                      </p>
                      <a
                        href="https://www.capterra.com/p/132178/RingCentral-Office/reviews/1864913/"
                        className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                      >
                        Verified Capterra Review
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
            <div className="max-w-[534px] switch lg:w-full -ml-2 lg:ml-0">
              <h3 className="font-bold lg:font-semibold text-[22px] md:text-[calc(18px+0.5vw)] leading-8 text-center mx-auto">
                Fast and responsive
              </h3>
              <Swiper
                pagination={{
                  clickable: true,
                  bulletClass:
                    "bg-blue w-2 h-2 rounded-full bg-opacity-20 mx-1 inline-block cursor-pointer",
                }}
                loop={true}
                spaceBetween={40}
                allowTouchMove={true}
                autoHeight={true}
                className="!pb-10 relative mt-10 lg:mt-3 !px-5"
              >
                <SwiperSlide>
                  <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                    <div className="w-[240px] h-8 mx-auto flex">
                      <Star1 width={33} />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                    </div>
                    <div className="min-h-[155px] lg:min-h-0 items-center mt-8 lg:mt-5">
                      <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center ">
                        "Really great app with easy setup and exceptional customer support, especially for faxing cloudly and contract files fastly. I regularly used the premium monthly membership. So impressed by this outstanding service."
                      </p>
                      <a
                        href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4360702"
                        className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                      >
                        Verified G2 Review
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                    <div className="w-[240px] h-8 mx-auto flex">
                      <Star1 width={33} />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                    </div>
                    <div className="min-h-[155px] lg:min-h-0 items-center mt-8 lg:mt-5">
                      <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                        “Cocofax brings the electric fax to the 21st Century!!! Sending and receiving the fax electronically saves a lot of time. There is no need for a fax machine. What's more, customer support responded very fast when I confused with some features.”
                      </p>
                      <a
                        href="https://www.g2.com/products/cocofax/reviews/cocofax-review-4264508"
                        className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                      >
                        Verified G2 Review
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className="mx-auto bg-white shadow-block hover:shadow-blockHover rounded pb-[60px] lg:pb-10 pt-[34px] lg:pt-5">
                    <div className="w-[240px] h-8 mx-auto flex">
                      <Star1 width={33} />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                      <Star1 width={33} className="ml-5" />
                    </div>
                    <div className="min-h-[155px] lg:min-h-0 items-center mt-8 lg:mt-5">
                      <p className="text-gray-dark leading-[26px] px-6 mx-auto text-center">
                        “Nothing dislike right now. Technical support reponses so fast whenever I got a problem.”
                      </p>
                      <a
                        href="https://www.capterra.com/p/204795/CocoFax/reviews/2954376/"
                        className="pt-3 text-blue mx-auto text-lg text-center hover:underline block"
                      >
                        Verified Capterra Review
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
          </div>
        </div>
      </section>
      {/* /content05 - CocoFax - What Do Our Customers Say About Us */}

      <section className="max-w-[1116px] w-full px-4 mx-auto pt-[96px] lg:pt-10">
        <div className="w-[184px] leading-8 flex items-center justify-center rounded-full mx-auto bg-green bg-opacity-[0.06] text-green text-sm font-bold py-1 px-[14px]">
          Best Online Fax
        </div>
        <h2 className="text-black text-[28px] md:text-[calc(18px+1.8vw)] font-extrabold text-center mt-[18px] leading-tight">
          Why Customers Love CocoFax?
        </h2>
        <div className="mx-auto grid grid-cols-2 gap-x-9 gap-y-[22px] lg:w-full lg:grid-cols-1 mt-10 lg:mt-5">
          <div className="mx-auto bg-white rounded-[10px] px-[18px] pt-[34px] lg:pt-5 pb-[42px] lg:pb-10 shadow-block hover:bg-hover-gray lg:w-full">
            <Faxing width={64} className="mx-auto" />
            <div className="text-2xl md:text-[calc(18px+1.5vw)] text-center font-bold pt-[31px] lg:pt-4 pb-[18px] lg:pb-4">
              Free faxing (no credit card needed)
            </div>
            <p className="text-gray-dark mx-auto text-center leading-7">
              CocoFax’s free fax service allows you to fax 10 pages for free without submitting any credit card information. That's right, trial our product with no strings attached and see if it suits your needs before committing yourself to anything. A perfect solution for those who only fax once in a blue moon.
            </p>
          </div>
          <div className="mx-auto bg-white rounded-[10px] px-[18px] pt-[34px] lg:pt-5 pb-[42px] lg:pb-10 shadow-block hover:bg-hover-gray lg:w-full">
            <Time width={64} className="mx-auto" />
            <div className="text-2xl md:text-[calc(18px+1.5vw)] text-center font-bold pt-[31px] lg:pt-4 pb-[18px] lg:pb-4">
              Time-saving and efficiency-boosting
            </div>
            <p className="text-gray-dark mx-auto text-center leading-7">
              CocoFax provides a full-fledged fax solution to help you <a href="/fax-from-computer.html" className="text-blue" target="_blank">fax from any internet-enabled device like computers</a>, eliminating the long wait times that often accompany traditional faxing. The email to fax technology also speeds up your business communications with clients and enhances efficiency.
            </p>
          </div>
          <div className="mx-auto bg-white rounded-[10px] px-[18px] pt-[34px] lg:pt-5 pb-[42px] lg:pb-10 shadow-block hover:bg-hover-gray lg:w-full">
            <Security width={64} className="mx-auto" />
            <div className="text-2xl md:text-[calc(18px+1.5vw)] text-center font-bold pt-[31px] lg:pt-4 pb-[18px] lg:pb-4">
              Unparalleled data security
            </div>
            <p className="text-gray-dark mx-auto text-center leading-7">
              Data security is CocoFax’s priority. Faxes sent via our platform are encrypted with 256-bit Advanced Encryption Systems (AES). Our adherence to <a href="/hipaa-compliant-fax.html" className="text-blue" target="_blank">HIPAA</a> and <a href="/phipa-fax.html" className="text-blue" target="_blank">PHIPA</a> standards ensures secrecy. Enable two-factor authentication to shield every bit of your sensitive information from data breaches.
            </p>
          </div>
          <div className="mx-auto bg-white rounded-[10px] px-[18px] pt-[34px] lg:pt-5 pb-[42px] lg:pb-10 shadow-block hover:bg-hover-gray lg:w-full">
            <Number width={64} className="mx-auto" />
            <div className="text-2xl md:text-[calc(18px+1.5vw)] text-center font-bold pt-[31px] lg:pt-4 pb-[18px] lg:pb-4">
              Hassle-free fax number porting
            </div>
            <p className="text-gray-dark mx-auto text-center leading-7">
              Retaining your current fax number is made easy with CocoFax. Whenever you wish to port your fax number to CocoFax, just fill out a simple form and CocoFax will take care of the rest. Save you hassles in updating business cards or email signatures.
            </p>
          </div>
        </div>
      </section>
      {/* content09 - Why Choose CocoFax Over eFax */}

      {/* foot */}
      <section css={[tw`flex justify-center items-center relative`, [styles.footBg]]}>
        <div className="max-w-[1163px] w-full px-4 mt-[97px] lg:mt-10 mb-[95px] lg:mb-10 flex items-center relative z-[1] lg:flex-col-reverse   ">
          <figure className="w-[600px] lg:w-full lg:mt-10">
            <StaticImage
              src="../images/e-fax/pc-mobile.png"
              width={1200}
              alt="cocofax PC and mobile"
            />
          </figure>
          <div className="ml-[70px] max-w-[460px] lg:ml-0 lg:mt-5 lg:text-center">
            <h2 className="font-extrabold text-5xl md:text-[calc(28px+1.8vw)] leading-snug">
              Get Started with CocoFax Today!
            </h2>
            <p className="mt-[22px] leading-6 text-gray-dark">
              No need to make do with the poor design, unsatisfying customer support, and unfriendly pricing plans of RingCentral. Onboard CocoFax now and speed up your faxing efficiency with CocoFax’s easy-to-use cloud fax services!
            </p>
            <div className="relative max-w-[200px] lg:mx-auto mt-10 lg:mt-5 cursor-pointer">
              <div className="text-center bg-[#F77062] hover:bg-[#ff5656] font-bold md:font-semibold shadow-redButton rounded-[3px] leading-[52px] px-5 md:px-2 block">
                <a
                  href="/choose-fax-number.html#/"
                  className="text-white hover:text-white"
                >
                  Start Faxing
                </a>
              </div>
              <p className="leading-8 text-xs text-center text-[#444]">
                30-Day Money-Back Guarantee
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* /foot */}
    </Layout>
  )
}
